/**
 * Month calendar — a faithful port of `renderGlobalCalendar`.
 *
 * Everything the legacy grid conveyed is preserved: the clinic-coloured tint
 * that brightens with availability, the pulsing pending-count bubble, the
 * offer and extra-hours pills, the today ring, the "منتهي" past marker, the
 * availability/booked footer, and navigation bounded to ±2 months.
 *
 * The one structural change: the legacy version re-derived all of this inside a
 * 900-line string template and then patched itself afterwards to add
 * "fully booked" glow in a `setTimeout`. Here the counts are computed once, up
 * front, and the cell renders from them.
 */

import { useMemo } from 'react';
import { Icon } from '../../../components/common';
import {
  ARABIC_MONTHS,
  BADGE_EXTRA,
  BADGE_EXTRA_SHADOW,
  BADGE_OFFER,
  BADGE_OFFER_SHADOW,
  BADGE_PENDING,
  BADGE_PENDING_SHADOW,
  DAY_CONFIRMED,
  DAY_IDLE,
  DAY_PENDING,
  WEEKDAY_HEADERS,
  clinicTintStyle,
  leadingBlanks,
} from './legacyTheme';
import { buildMonthStats } from '../../../domain/calendar';
import { todayKey } from '../../../domain/dates';
import { MODE_ICONS } from '../../../domain/discovery';
import { useI18n } from '../../../i18n/I18nProvider';

export function DoctorCalendar({
  year,
  month,
  clinics = [],
  bookings = [],
  offers = [],
  extraHours = [],
  cancelledDays = [],
  onSelectDay,
  onNavigateMonth,
}) {
  const today = todayKey();

  const stats = useMemo(
    () => buildMonthStats({ year, month, clinics, bookings, offers, extraHours }),
    [year, month, clinics, bookings, offers, extraHours],
  );

  // المرحلة 1.3: the day's video and voice visits, marked on its cell.
  const remote = useMemo(() => {
    const byDay = new Map();
    for (const b of bookings) {
      if (b.status === 'cancelled' || (b.consultMode !== 'video' && b.consultMode !== 'voice')) continue;
      const day = String(b.date).slice(0, 10);
      const entry = byDay.get(day) || { video: 0, voice: 0 };
      entry[b.consultMode] += 1;
      byDay.set(day, entry);
    }
    return byDay;
  }, [bookings]);

  // The legacy calendar allowed ±2 months around today and disabled the arrows
  // outside that window.
  const { canGoPrev, canGoNext } = useMemo(() => {
    const now = new Date();
    const current = new Date(year, month, 1);
    const min = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const max = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    return { canGoPrev: current > min, canGoNext: current < max };
  }, [year, month]);

  const blanks = leadingBlanks(new Date(year, month, 1).getDay());
  const cells = [...stats.values()];

  return (
    <div className="glass-panel relative mb-8 overflow-hidden rounded-2xl border border-white/5 p-3 sm:p-6">
      <header className="mb-4 flex items-center justify-between gap-2 sm:mb-6">
        <h3 className="flex items-center gap-2 text-xl font-bold text-white">
          <Icon name="calendar-range" size={24} className="text-purple-400" />
          جدول مواعيد العيادات
          <span className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs font-normal text-slate-400">
            {ARABIC_MONTHS[month]} {year}
          </span>
        </h3>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canGoPrev}
            onClick={() => onNavigateMonth?.(-1)}
            title="الشهر السابق"
            className={`rounded-lg p-2 transition-colors ${
              canGoPrev
                ? 'cursor-pointer bg-slate-700 text-white hover:bg-slate-600'
                : 'cursor-not-allowed bg-slate-800 text-slate-500'
            }`}
          >
            <Icon name="chevron-right" size={20} />
          </button>
          <button
            type="button"
            disabled={!canGoNext}
            onClick={() => onNavigateMonth?.(1)}
            title="الشهر التالي"
            className={`rounded-lg p-2 transition-colors ${
              canGoNext
                ? 'cursor-pointer bg-slate-700 text-white hover:bg-slate-600'
                : 'cursor-not-allowed bg-slate-800 text-slate-500'
            }`}
          >
            <Icon name="chevron-left" size={20} />
          </button>
        </div>
      </header>

      {/* On a phone a day is about 40px wide: the weekday names shrink to their
          article and each cell keeps only what still reads at that size. */}
      <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs font-bold text-slate-500 sm:gap-2">
        {WEEKDAY_HEADERS.map((label) => (
          <div key={label} title={label}>
            <span className="text-[10px] sm:hidden">{label.replace(/^ال/, '')}</span>
            <span className="hidden sm:inline">{label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {Array.from({ length: blanks }, (_, i) => (
          <div key={`blank-${i}`} className="aspect-[4/5] bg-transparent sm:aspect-square" />
        ))}

        {cells.map((cell) => (
          <DayCell
            key={cell.dateKey}
            cell={cell}
            isToday={cell.dateKey === today}
            isPast={cell.dateKey < today}
            isCancelled={cancelledDays.includes(cell.dateKey)}
            remote={remote.get(cell.dateKey)}
            onSelect={() => onSelectDay?.(cell.dateKey)}
          />
        ))}
      </div>
    </div>
  );
}

/* ========================================================================== */

function DayCell({ cell, isToday, isPast, isCancelled, remote, onSelect }) {
  const { t } = useI18n();
  const {
    day,
    totalSlots,
    bookedSlots,
    availableSlots,
    pendingCount,
    confirmedCount,
    hasOffer,
    offerPercent,
    hasExtraHours,
    extraDiscount,
    workingClinics,
    isFull,
  } = cell;

  // Tint precedence, matching the legacy override order: past wins, then
  // pending (red), then all-confirmed (indigo), then the clinic colour.
  let style;
  let hoverClass = 'hover:bg-slate-800/80';

  if (isPast) {
    style = {};
    hoverClass = 'grayscale opacity-60 bg-slate-900/50 hover:bg-slate-800 border-slate-800';
  } else if (pendingCount > 0) {
    style = DAY_PENDING;
    hoverClass = 'hover:bg-red-500/20';
  } else if (confirmedCount > 0) {
    style = DAY_CONFIRMED;
    hoverClass = 'hover:bg-indigo-500/20';
  } else if (workingClinics.length > 0) {
    const availabilityPercent = totalSlots > 0 ? (availableSlots / totalSlots) * 100 : 0;
    style = clinicTintStyle(workingClinics[0].color || 'cyan', availabilityPercent);
  } else {
    style = DAY_IDLE;
  }

  const showStats = workingClinics.length > 0 || hasExtraHours;
  const offerLabel = offerPercent > 0 ? `عرض خصم نشط (خصم ${offerPercent}%)` : 'عرض خصم نشط';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      title={`${cell.dateKey} — ${availableSlots} متاح، ${bookedSlots} محجوز`}
      className={`group relative aspect-[4/5] cursor-pointer rounded-lg border transition-all sm:aspect-square sm:rounded-xl ${hoverClass} ${
        isToday ? 'shadow-[0_0_15px_rgba(6,182,212,0.15)] ring-2 ring-cyan-400' : ''
      }`}
      style={style}
    >
      {/* Offer pill — heartbeat unless the day has passed */}
      {hasOffer && (
        <div
          className={`absolute end-0.5 start-0.5 top-[34%] z-20 flex justify-center sm:end-1 sm:start-1 sm:top-[28%] ${
            isPast ? 'opacity-50' : 'animate-heartbeat'
          }`}
        >
          <div
            className="flex items-center gap-1.5 rounded-full px-1 py-0.5 text-[9px] font-black leading-none text-white shadow-lg sm:px-2.5 sm:text-[10px]"
            style={{ background: BADGE_OFFER, boxShadow: BADGE_OFFER_SHADOW, maxWidth: 'calc(100% - 4px)' }}
            title={offerLabel}
          >
            <span className="whitespace-nowrap sm:hidden">{offerPercent > 0 ? `${offerPercent}%` : '%'}</span>
            <span className="hidden overflow-hidden text-ellipsis whitespace-nowrap sm:inline">{offerLabel}</span>
          </div>
        </div>
      )}

      {/* Extra-hours pill — drops below the offer pill when both are present */}
      {hasExtraHours && (
        <div
          className={`absolute end-0.5 start-0.5 z-20 flex justify-center sm:end-1 sm:start-1 ${
            hasOffer ? 'top-[55%] sm:top-[52%]' : 'top-[34%] sm:top-[28%]'
          } ${isPast ? 'opacity-40' : 'animate-heartbeat'}`}
        >
          <div
            className="flex items-center gap-1.5 rounded-full px-1 py-0.5 text-[10px] font-black text-white shadow-lg sm:px-2.5"
            style={{ background: BADGE_EXTRA, boxShadow: BADGE_EXTRA_SHADOW, maxWidth: 'calc(100% - 4px)' }}
            title="ساعات إضافية"
          >
            <Icon name="zap" size={10} className="shrink-0 fill-white" />
            <span className="hidden whitespace-nowrap sm:inline">
              ساعات إضافية{extraDiscount > 0 ? ` · ${extraDiscount}%` : ''}
            </span>
          </div>
        </div>
      )}

      {/* Pending-count bubble — the pulse is what draws the eye to unpaid deposits */}
      {!isPast && pendingCount > 0 && (
        <div
          className="pending-badge-anim absolute -start-1.5 -top-1.5 z-30 sm:-start-2.5 sm:-top-2.5"
          title={`${pendingCount} حجز في انتظار تأكيد الدفع`}
        >
          <div
            className="flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-black text-white shadow-lg sm:h-6 sm:w-6 sm:text-[11px]"
            style={{ background: BADGE_PENDING, boxShadow: BADGE_PENDING_SHADOW }}
          >
            {pendingCount}
          </div>
        </div>
      )}

      {/* Fully-booked marker */}
      {!isPast && isFull && (
        <div className="absolute end-1 top-1 z-20 hidden rounded bg-red-500 px-1 py-0.5 text-[8px] font-bold leading-none text-onaccent sm:block">
          مكتمل
        </div>
      )}

      {/* المرحلة 1.3: the day's video and voice visits. */}
      {remote && !isPast && (
        <div
          className={`absolute end-1 z-20 flex items-center gap-1 sm:end-1.5 ${isFull ? 'top-1 sm:top-6' : 'top-1 sm:top-1.5'}`}
          data-testid="calendar-remote"
        >
          {['video', 'voice'].filter((m) => remote[m] > 0).map((m) => (
            <span key={m} className={`mode-badge mode-badge--${m} !px-1 !py-0`} title={t('calendar13.remote', { n: remote[m], mode: t(`discovery.mode.${m}`) })}>
              <Icon name={MODE_ICONS[m]} size={9} />
              <span className="hidden sm:inline">{remote[m]}</span>
            </span>
          ))}
        </div>
      )}

      <div
        className={`absolute start-1.5 top-1 z-10 flex items-center gap-0.5 text-[11px] font-bold sm:start-2 sm:top-2 sm:text-xs ${
          isPast ? 'text-slate-600' : isToday ? 'text-cyan-400' : 'text-slate-400'
        }`}
      >
        {day}
      </div>

      {isPast && (
        <div className="absolute bottom-2 end-2 hidden text-[10px] font-bold text-slate-600 sm:block">منتهي</div>
      )}

      {/* A phone shows the free count alone: a green dot and a number. */}
      {showStats && !isPast && (
        <div className="absolute bottom-1 end-0 start-0 flex items-center justify-center gap-1 text-[10px] font-bold text-emerald-400 sm:hidden" title={`${availableSlots} متاح، ${bookedSlots} محجوز`}>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {availableSlots}
        </div>
      )}
      {showStats && !isPast && (
        <div className="absolute bottom-2 end-2 start-2 hidden items-center justify-between rounded-lg border border-white/5 bg-slate-900/80 px-2 py-1 text-[9px] font-bold text-slate-300 shadow-lg backdrop-blur-sm sm:flex">
          <span className="flex items-center gap-1 text-emerald-400" title="متاح">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]" />
            {availableSlots} متاح
          </span>
          <span className="text-xs font-thin text-slate-600">|</span>
          <span className="flex items-center gap-1 text-rose-400" title="محجوز">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500 shadow-[0_0_5px_rgba(244,63,94,0.5)]" />
            {bookedSlots} محجوز
          </span>
        </div>
      )}

      {isCancelled && (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-black/60 backdrop-blur-sm"
          title="هذا اليوم ملغي"
        >
          <div className="text-2xl font-bold text-white opacity-50 sm:text-5xl">❌</div>
        </div>
      )}
    </div>
  );
}

export default DoctorCalendar;
